/**
 * RENDER — owned by the Render agent. Three.js view layer.
 *
 * Pure consumer of contracts. Builds meshes from a CourseSpec once, then on each
 * animation frame reads a GameState snapshot and positions the marble/camera.
 * Render owns NO physics state and must never mutate a GameState — it is given a
 * read-only snapshot per frame.
 *
 * This file is the LOCKED PUBLIC SURFACE of Render. The Render agent fills in the
 * bodies.
 *
 * ── How interpolation works ─────────────────────────────────────────────────────
 * Core steps at a FIXED 60Hz tick; the display may refresh faster. `render(state,
 * alpha)` is called every animation frame with the LATEST snapshot and an `alpha`
 * in [0,1] for the fraction between the previous and current tick. We keep a tiny
 * one-snapshot history: when `state.tick` advances we roll "current → previous", so
 * within a tick we interpolate marble pose between the two with `alpha`, and movers
 * are recomputed as a pure function of (tick + alpha) per the MoverMotion contract.
 * We copy only primitives out of GameState into our own scratch — never mutate it.
 */

import * as THREE from 'three';
import type { CourseSpec, GameState, Transform, Vec3 } from '@/types';
import { PALETTE } from './theme';
import { CourseView } from './course';
import { Environment } from './environment';
import { Marble } from './marble';
import { Checkpoints } from './checkpoints';
import { ChaseCamera } from './camera';
import { SpeedFx } from './fx';
import {
  clamp,
  lerpVec3,
  nlerpQuat,
  type MVec3,
  type MQuat,
} from './math';

export interface Renderer {
  /** Build the static world (meshes, lights, materials) from the spec. Call once. */
  buildCourse(spec: CourseSpec): void;
  /**
   * Draw one frame. `state` is the latest Core snapshot; `alpha` in [0,1] is the
   * interpolation factor between the previous and current tick for smooth motion
   * at display refresh rates above the sim rate.
   */
  render(state: GameState, alpha: number): void;
  resize(width: number, height: number): void;
  dispose(): void;
}

/** Our private snapshot of the bits of GameState we interpolate (never the contract object). */
interface PoseSnapshot {
  tick: number;
  readonly position: MVec3;
  readonly rotation: MQuat;
  readonly velocity: MVec3;
}

function copyPose(out: PoseSnapshot, marble: Transform, velocity: Vec3, tick: number): void {
  out.tick = tick;
  out.position[0] = marble.position[0];
  out.position[1] = marble.position[1];
  out.position[2] = marble.position[2];
  out.rotation[0] = marble.rotation[0];
  out.rotation[1] = marble.rotation[1];
  out.rotation[2] = marble.rotation[2];
  out.rotation[3] = marble.rotation[3];
  out.velocity[0] = velocity[0];
  out.velocity[1] = velocity[1];
  out.velocity[2] = velocity[2];
}

function makeSnapshot(): PoseSnapshot {
  return { tick: -1, position: [0, 0, 0], rotation: [0, 0, 0, 1], velocity: [0, 0, 0] };
}

/** Copy one PoseSnapshot into another (history roll). */
function copySnapshot(out: PoseSnapshot, src: PoseSnapshot): void {
  out.tick = src.tick;
  out.position[0] = src.position[0];
  out.position[1] = src.position[1];
  out.position[2] = src.position[2];
  out.rotation[0] = src.rotation[0];
  out.rotation[1] = src.rotation[1];
  out.rotation[2] = src.rotation[2];
  out.rotation[3] = src.rotation[3];
  out.velocity[0] = src.velocity[0];
  out.velocity[1] = src.velocity[1];
  out.velocity[2] = src.velocity[2];
}

/** Cap device pixel ratio so we stay in the mobile fill-rate budget. */
const MAX_PIXEL_RATIO = 2;

class RendererImpl implements Renderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly environment: Environment;

  private camera: ChaseCamera;
  private fx: SpeedFx | null = null;
  private course: CourseView | null = null;
  private marble: Marble | null = null;
  private checkpoints: Checkpoints | null = null;

  private spec: CourseSpec | null = null;

  // Interpolation history.
  private readonly prev = makeSnapshot();
  private readonly curr = makeSnapshot();
  private haveCurr = false;

  // Per-frame scratch (no allocation in render()).
  private readonly lerpPos: MVec3 = [0, 0, 0];
  private readonly lerpRot: MQuat = [0, 0, 0, 1];
  private readonly lerpVel: MVec3 = [0, 0, 0];

  // Wall-clock delta for framerate-independent smoothing (camera/FX), not the sim.
  private lastFrameTime = -1;

  private width = 1;
  private height = 1;

  constructor(canvas: HTMLCanvasElement) {
    const supportsAA = detectAntialias();
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: supportsAA,
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setClearColor(PALETTE.void, 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(PALETTE.void);
    // Fog hides the far edges into the void and reinforces depth/speed cheaply.
    this.scene.fog = new THREE.Fog(PALETTE.void, 20, 90);

    this.environment = new Environment();
    this.environment.attach(this.scene);
    this.environment.buildEnvironment(this.renderer, this.scene);

    const w = canvas.clientWidth || canvas.width || 1;
    const h = canvas.clientHeight || canvas.height || 1;
    this.width = w;
    this.height = h;
    this.camera = new ChaseCamera(w / h, 30);
    this.renderer.setSize(w, h, false);
  }

  buildCourse(spec: CourseSpec): void {
    // Tear down any previously-built course so rebuild is leak-free.
    this.teardownCourse();

    this.spec = spec;

    this.course = new CourseView(spec);
    this.scene.add(this.course.group);

    this.marble = new Marble(spec.tuning.marbleRadius);
    this.scene.add(this.marble.group);

    this.checkpoints = new Checkpoints(spec.checkpoints);
    this.scene.add(this.checkpoints.group);

    // Rebuild a camera sized to the current viewport with this course's maxSpeed.
    this.camera = new ChaseCamera(this.width / this.height, spec.tuning.maxSpeed);
    this.camera.snapTo(spec.spawn.position);

    this.fx = new SpeedFx(spec.tuning.maxSpeed);
    this.fx.setAspect(this.width / this.height);

    // Frame fog to the course's depth so distant geometry fades into the void.
    const depth = spec.bounds.max[2] - spec.bounds.min[2];
    const near = Math.max(8, depth * 0.18);
    const far = Math.max(near + 20, depth * 0.85);
    (this.scene.fog as THREE.Fog).near = near;
    (this.scene.fog as THREE.Fog).far = far;

    // Reset interpolation history to the spawn pose.
    copyPose(this.prev, spec.spawn, ZERO_VEL, 0);
    copyPose(this.curr, spec.spawn, ZERO_VEL, 0);
    this.haveCurr = true;
    this.lastFrameTime = -1;
  }

  render(state: GameState, alpha: number): void {
    if (!this.spec || !this.marble || !this.course || !this.checkpoints || !this.fx) {
      // Nothing built yet; nothing to draw. (Shell builds before rendering.)
      return;
    }
    const a = clamp(alpha, 0, 1);

    // Roll the interpolation history when a new sim tick arrives.
    if (!this.haveCurr) {
      copyPose(this.curr, state.marble, state.velocity, state.tick);
      copyPose(this.prev, state.marble, state.velocity, state.tick);
      this.haveCurr = true;
    } else if (state.tick !== this.curr.tick) {
      // current → previous, then adopt the new snapshot as current.
      copySnapshot(this.prev, this.curr);
      copyPose(this.curr, state.marble, state.velocity, state.tick);
    } else {
      // Same tick, possibly updated velocity/pose (e.g. paused frame): refresh current.
      copyPose(this.curr, state.marble, state.velocity, state.tick);
    }

    // Wall-clock dt for smoothing (NOT the sim clock).
    const now = nowSeconds();
    const dt = this.lastFrameTime < 0 ? 1 / 60 : clamp(now - this.lastFrameTime, 0, 0.1);
    this.lastFrameTime = now;

    // Interpolate marble pose + velocity between previous and current tick.
    lerpVec3(this.lerpPos, this.prev.position, this.curr.position, a);
    nlerpQuat(this.lerpRot, this.prev.rotation, this.curr.rotation, a);
    lerpVec3(this.lerpVel, this.prev.velocity, this.curr.velocity, a);

    // Movers: pure function of fractional tick (curr.tick - 1 + alpha gives the
    // sub-tick position between the previous and current sim ticks).
    const fracTick = this.prev.tick + (this.curr.tick - this.prev.tick) * a;
    this.course.updateMovers(fracTick);

    // Marble appearance (position + cosmetic roll from interpolated velocity).
    this.marble.update(this.lerpPos, this.lerpVel, dt);
    // Apply the interpolated marble rotation on top (sim pose, if Core sets it).
    // Cosmetic roll already drives the mesh; we leave group orientation upright so
    // the contact shadow stays flat. (Sim rotation is reflected via roll dynamics.)

    // Checkpoints (state from GameState.checkpointIndex; pulse tick-locked).
    this.checkpoints.update(state.checkpointIndex, fracTick, dt);

    // FX derived from velocity + owned geometry → camera kick + FOV kick.
    const frame = this.fx.update(
      state,
      this.lerpPos,
      this.course.wallVolumes,
      this.course.boostVolumes,
      dt,
    );
    this.camera.applyKick(frame.kick);
    this.camera.applyFovKick(frame.fovKick);

    // Marble emissive pulse mirrors the FX flash (cyan near-miss / amber boost).
    if (frame.marbleFlashColor >= 0 && frame.marbleFlashIntensity > 0) {
      this.marble.setEmissivePulse(frame.marbleFlashColor, frame.marbleFlashIntensity * 0.9);
    } else {
      this.marble.setEmissivePulse(0x000000, 0);
    }

    // Chase camera follows the interpolated marble + velocity.
    this.camera.update(this.lerpPos, this.lerpVel, dt);

    // Draw main scene, then the speed overlay on top (same frame, no clear).
    this.renderer.render(this.scene, this.camera.camera);
    this.fx.render(this.renderer);
  }

  resize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
    this.renderer.setSize(this.width, this.height, false);
    const aspect = this.width / this.height;
    this.camera.setAspect(aspect);
    this.fx?.setAspect(aspect);
  }

  dispose(): void {
    this.teardownCourse();
    this.environment.dispose(this.scene);
    this.scene.background = null;
    this.scene.fog = null;
    // Release the GL context + all remaining GPU resources.
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  private teardownCourse(): void {
    if (this.course) {
      this.course.dispose();
      this.course = null;
    }
    if (this.marble) {
      this.marble.dispose();
      this.marble = null;
    }
    if (this.checkpoints) {
      this.checkpoints.dispose();
      this.checkpoints = null;
    }
    if (this.fx) {
      this.fx.dispose();
      this.fx = null;
    }
    this.haveCurr = false;
    this.spec = null;
  }
}

const ZERO_VEL: Vec3 = [0, 0, 0];

/** High-resolution seconds for smoothing deltas. */
function nowSeconds(): number {
  return (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
}

/** Heuristic: enable MSAA only on devices likely to afford it (desktop / hi-DPI). */
function detectAntialias(): boolean {
  if (typeof window === 'undefined') return false;
  const dpr = window.devicePixelRatio || 1;
  // On phones (high DPR) we lean on the capped resolution instead of MSAA.
  return dpr < 2;
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  return new RendererImpl(canvas);
}
